import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// ============================================================================
// One shared "background runs" system for the Catalog Manager.
//
// Historically each long-running action rolled its own in-flight indicator: the
// ingest chain used the bottom-right tray, while the grocery replacement run and
// the web sourcing finder each rendered their own floating pill (bottom-left).
// That's confusing. Now every backgroundable run registers here and shows up in
// the SAME place (the bottom-right runs tray) with the same view/cancel affordances.
// ============================================================================

/** A transient in-flight run (replacement, sourcing finder, ...). */
export interface BackgroundRun {
  /** Stable id for the run (one per source; re-registering updates in place). */
  id: string;
  /** Short title, e.g. "Finding replacements". */
  title: string;
  /** Optional live detail line, e.g. the current step. */
  detail?: string;
  /** Bring the run's own progress dialog/overlay back to the foreground. */
  onView?: () => void;
  /** Cancel the run. */
  onCancel?: () => void;
}

interface BackgroundRunsContextValue {
  runs: BackgroundRun[];
  upsertRun: (run: BackgroundRun) => void;
  removeRun: (id: string) => void;
}

const BackgroundRunsContext = createContext<BackgroundRunsContextValue | null>(
  null
);

export const BackgroundRunsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [map, setMap] = useState<Record<string, BackgroundRun>>({});

  const upsertRun = useCallback((run: BackgroundRun) => {
    setMap((m) => ({ ...m, [run.id]: run }));
  }, []);

  const removeRun = useCallback((id: string) => {
    setMap((m) => {
      if (!(id in m)) return m;
      const next = { ...m };
      delete next[id];
      return next;
    });
  }, []);

  const runs = useMemo(() => Object.values(map), [map]);
  const value = useMemo(
    () => ({ runs, upsertRun, removeRun }),
    [runs, upsertRun, removeRun]
  );

  return (
    <BackgroundRunsContext.Provider value={value}>
      {children}
    </BackgroundRunsContext.Provider>
  );
};

export const useBackgroundRuns = (): BackgroundRunsContextValue => {
  const ctx = useContext(BackgroundRunsContext);
  if (!ctx) {
    throw new Error(
      "useBackgroundRuns must be used within a BackgroundRunsProvider"
    );
  }
  return ctx;
};

/**
 * Register a run in the shared tray while `active` is true, and remove it when
 * `active` goes false or the component unmounts. `title`/`detail` changes update
 * the tray entry in place; `onView`/`onCancel` are always called fresh (kept in
 * refs) so the effect doesn't churn on every render.
 */
export const useRegisterBackgroundRun = (
  active: boolean,
  run: BackgroundRun
): void => {
  const { upsertRun, removeRun } = useBackgroundRuns();
  const { id, title, detail, onView, onCancel } = run;

  const onViewRef = useRef(onView);
  const onCancelRef = useRef(onCancel);
  onViewRef.current = onView;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!active) return undefined;
    upsertRun({
      id,
      title,
      detail,
      onView: () => onViewRef.current?.(),
      onCancel: () => onCancelRef.current?.(),
    });
    return () => removeRun(id);
  }, [active, id, title, detail, upsertRun, removeRun]);
};
